import "leaflet/dist/leaflet.css"
import L from "leaflet"
import { useEffect, useRef, useState } from "preact/hooks"
import type { Item } from "./EditableList"
import type { GeocodedItem } from "./lib/geocode"
import { resolveItem } from "./lib/geocode"
import CircularProgress from "@mui/material/CircularProgress"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Paper from "@mui/material/Paper"

// Leaflet's default icon images are broken when bundled with Vite; supply them manually.
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: new URL("leaflet/dist/images/marker-icon-2x.png", import.meta.url).href,
  iconUrl: new URL("leaflet/dist/images/marker-icon.png", import.meta.url).href,
  shadowUrl: new URL("leaflet/dist/images/marker-shadow.png", import.meta.url).href,
})

export function MapView({ items }: { items: Item[] }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const [geocoded, setGeocoded] = useState<GeocodedItem[]>([])
  const [loading, setLoading] = useState(true)

  // Geocode all candidates on mount / when items change
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all(
      items
        .filter((item) => item.content)
        .map((item) => resolveItem(item.id, item.content!)),
    ).then((results) => {
      if (cancelled) return
      setGeocoded(results.filter((r): r is GeocodedItem => r !== null))
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [items])

  // Initialize and update the Leaflet map
  useEffect(() => {
    if (!containerRef.current || geocoded.length === 0) return

    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current)
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(mapRef.current)
    }

    const map = mapRef.current

    // Clear existing markers
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) map.removeLayer(layer)
    })

    // Add a marker for each geocoded item
    const markers = geocoded.map(({ latlng, label }) =>
      L.marker([latlng.lat, latlng.lng]).bindPopup(label).addTo(map),
    )

    // Fit map to show all markers
    const first = geocoded[0]
    if (markers.length === 1 && first) {
      map.setView([first.latlng.lat, first.latlng.lng], 14)
    } else {
      const bounds = L.latLngBounds(geocoded.map(({ latlng }) => [latlng.lat, latlng.lng]))
      map.fitBounds(bounds, { padding: [40, 40] })
    }

    return () => {
      // Clean up markers when geocoded list changes
      markers.forEach((m) => map.removeLayer(m))
    }
  }, [geocoded])

  // Destroy map on unmount
  useEffect(() => {
    return () => {
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [])

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Locating candidates…
        </Typography>
      </Stack>
    )
  }

  if (geocoded.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh", px: 3 }}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          No mappable candidates found. Add options with addresses or Google Maps
          links to see them here.
        </Typography>
      </Stack>
    )
  }

  return (
    <Paper
      elevation={0}
      sx={{ position: "relative", flexGrow: 1, minHeight: "60vh" }}
    >
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100%", minHeight: "60vh" }}
      />
    </Paper>
  )
}
